import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "@/lib/router-compat";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, Clock, User, Languages } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import MarkdownContent from "@/components/blog/MarkdownContent";
import BlogFAQ, { type FAQItem } from "@/components/blog/BlogFAQ";


import RelatedPosts from "@/components/blog/RelatedPosts";
import ReadingProgress from "@/components/blog/ReadingProgress";
import StickyArticleCTA from "@/components/blog/StickyArticleCTA";
import { useTranslations } from "@/hooks/useTranslations";
import { useSeo } from "@/hooks/useSeo";
import { useLanguage } from "@/contexts/LanguageContext";
// Load the backend client on demand so its ~60 KB stay out of the initial bundle.
const getSupabase = async () => (await import("@/integrations/supabase/client")).supabase;
import { blogBasePath, formatPostDate, localizePost, type LocalizedPost } from "@/lib/blog";
import { trackBlogView } from "@/lib/analytics";
import type { BlogPostWithCategory } from "@/lib/blogPostRoute";

const BlogPost = ({ initialRow }: { initialRow?: BlogPostWithCategory | null } = {}) => {
  const { slug } = useParams<{ slug: string }>();
  const t = useTranslations();
  const { language, setLanguage } = useLanguage();


  const { data: postRow, isLoading } = useQuery({
    queryKey: ["blog_post", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await (await getSupabase())
        .from("blog_posts")
        .select("*, blog_categories(*)")
        .eq("slug", slug)
        .lte("published_at", new Date().toISOString())
        .not("published_at", "is", null)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
    // Server loader already fetched this post, so SSR HTML contains the full article.
    initialData: initialRow && initialRow.slug === slug ? initialRow : undefined,
  });

  const post: LocalizedPost | null = useMemo(
    () => (postRow ? localizePost(postRow as Parameters<typeof localizePost>[0], language) : null),
    [postRow, language],
  );

  // Track page view once per post load
  useEffect(() => {
    if (!post) return;
    void trackBlogView(post.id, {
      slug: post.slug,
      title: post.title,
      language,
    });
  }, [post?.id, language]);

  const { data: relatedRows = [] } = useQuery({
    queryKey: ["blog_related", postRow?.category_id, postRow?.id],
    queryFn: async () => {
      if (!postRow?.category_id || !postRow?.id) return [];
      const { data, error } = await (await getSupabase())
        .from("blog_posts")
        .select("*, blog_categories(*)")
        .eq("category_id", postRow.category_id)
        .neq("id", postRow.id)
        .lte("published_at", new Date().toISOString())
        .not("published_at", "is", null)
        .order("published_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
    enabled: !!postRow?.category_id,
  });

  const relatedPosts: LocalizedPost[] = useMemo(
    () => relatedRows.map((r) => localizePost(r as Parameters<typeof localizePost>[0], language)),
    [relatedRows, language],
  );

  const [liveFaq, setLiveFaq] = useState<FAQItem[] | null>(null);
  const faqForSeo: FAQItem[] = liveFaq ?? post?.faq ?? [];

  const canonicalUrl = post
    ? `${typeof window !== "undefined" ? window.location.origin : ""}${blogBasePath(language)}/${post.slug}`
    : "";

  const jsonLd = useMemo(() => {
    if (!post) return undefined;
    const article: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: (post.seo_title || post.title || "").slice(0, 110),
      description: post.seo_description || post.excerpt || undefined,
      datePublished: post.published_at || undefined,
      dateModified: post.updated_at || post.published_at || undefined,
      author: {
        "@type": "Person",
        name: post.author_name,
        url: "https://agapeco.net/about-us",
      },
      publisher: {
        "@type": "Organization",
        name: "AGAPE Automation Systems",
        logo: {
          "@type": "ImageObject",
          url: "https://agapeco.net/agape-logo.png",
          width: 512,
          height: 512,
        },
      },
      image: post.cover_image_url
        ? [
            {
              "@type": "ImageObject",
              url: post.cover_image_url,
              width: 1280,
              height: 720,
            },
          ]
        : undefined,
      inLanguage: language,
      url: canonicalUrl || undefined,
      mainEntityOfPage: canonicalUrl
        ? { "@type": "WebPage", "@id": canonicalUrl }
        : undefined,
    };
    // Strip undefined so Rich Results doesn't complain about empty fields
    for (const k of Object.keys(article)) {
      if (article[k] === undefined) delete article[k];
    }
    if (faqForSeo.length === 0) return article;
    const faqPage: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqForSeo
        .filter((item) => item.q && item.a)
        .map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
    };
    return [article, faqPage];
  }, [post, faqForSeo, language, canonicalUrl]);

  // Answer engines penalise a modified date older than the published date.
  const freshestDate =
    post?.updated_at && post?.published_at
      ? (new Date(post.updated_at) > new Date(post.published_at)
          ? post.updated_at
          : post.published_at)
      : post?.updated_at || post?.published_at || undefined;

  useSeo({
    title: post?.seo_title || "AGAPE Blog",
    description: post?.seo_description || "Insights on AI, automation, and SME growth from AGAPE.",
    canonicalPath: post ? `${blogBasePath(language)}/${post.slug}` : undefined,
    ogImage: post?.cover_image_url || undefined,
    preloadImage: post?.cover_image_url || undefined,
    ogType: "article",
    publishedTime: post?.published_at || undefined,
    modifiedTime: freshestDate,
    locale: language === "pl" ? "pl-PL" : language === "by" ? "be-BY" : "en-US",
    jsonLd,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-32 pb-20 container mx-auto px-6 max-w-3xl">
          <div className="h-8 w-40 bg-muted/40 rounded-sm animate-pulse mb-6" />
          <div className="h-12 w-full bg-muted/40 rounded-sm animate-pulse mb-4" />
          <div className="h-12 w-3/4 bg-muted/40 rounded-sm animate-pulse mb-10" />
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-4 w-full bg-muted/30 rounded-sm animate-pulse" />
            ))}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-32 pb-32 container mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">{t.blog.notFoundTitle}</h1>
          <p className="text-foreground/70 mb-8">{t.blog.notFoundSubtitle}</p>
          <Link
            to={blogBasePath(language)}
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80"
          >
            <ArrowLeft size={20} />
            {t.blog.back}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-20">
      <Navigation />
      <ReadingProgress
        targetSelector="article"
        postId={post.id}
        postSlug={post.slug}
        postTitle={post.title}
        language={language}
      />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-12 bg-gradient-to-br from-primary/10 via-background to-accent/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(139,92,246,0.12),transparent_50%)]" />
        <div className="container mx-auto px-6 relative z-10 max-w-4xl">
          <Link
            to={blogBasePath(language)}
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            {t.blog.back}
          </Link>

          {post.category && (
            <span
              className="inline-block text-xs uppercase tracking-wide font-semibold mb-4 px-3 py-1 rounded-full"
              style={{
                backgroundColor: post.category.accent_color
                  ? `${post.category.accent_color}1f`
                  : "hsl(var(--primary) / 0.12)",
                color: post.category.accent_color || "hsl(var(--primary))",
              }}
            >
              {post.category.name}
            </span>
          )}

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span className="gradient-text">{post.title}</span>
          </h1>

          {post.excerpt && (
            <p className="text-lg md:text-xl text-foreground/80 mb-6">{post.excerpt}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground/70">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author_name}
            </span>
            {post.published_at && (
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.published_at}>
                  {formatPostDate(post.published_at, language)}
                </time>
              </span>
            )}
            {post.updated_at &&
              post.published_at &&
              new Date(post.updated_at) > new Date(post.published_at) && (
                <span className="flex items-center gap-2 text-foreground/60">
                  {t.blog.updatedOn}{" "}
                  <time dateTime={post.updated_at}>
                    {formatPostDate(post.updated_at, language)}
                  </time>
                </span>
              )}
            {post.reading_time_minutes ? (
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.reading_time_minutes} {t.blog.minutes}
              </span>
            ) : null}
            {post.is_fallback && (
              <span className="flex items-center gap-2 text-muted-foreground">
                <Languages className="w-4 h-4" />
                {t.blog.fallbackNotice}
              </span>
            )}
            {language !== "en" && post.has_english && !post.is_fallback && (
              <button
                type="button"
                onClick={() => setLanguage("en")}
                className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors underline-offset-4 hover:underline"
              >
                <Languages className="w-4 h-4" />
                {t.blog.readInEnglish}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Cover image — LCP candidate: preloaded via useSeo and marked high priority */}
      {post.cover_image_url && (
        <div className="container mx-auto px-6 max-w-4xl -mt-2">
          <img
            src={post.cover_image_url}
            alt={post.title}
            width={1280}
            height={720}
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="w-full rounded-2xl shadow-xl aspect-[16/9] object-cover"
          />
        </div>
      )}

      {/* Content */}
      <article className="py-16">
        <div className="container mx-auto px-6 max-w-3xl">
          <MarkdownContent>{post.content}</MarkdownContent>

          <p className="mt-10 text-sm text-foreground/70 border-t border-border/60 pt-6">
            {t.blog.libertyNotePre}
            <Link
              to={language === "pl" ? "/pl/liberty" : language === "by" ? "/by/liberty" : "/liberty"}
              className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80"
            >
              {t.blog.libertyNoteLink}
            </Link>
            {t.blog.libertyNotePost}
          </p>


          <BlogFAQ
            postId={post.id}
            language={language}
            initialFaq={post.faq && post.faq.length > 0 ? post.faq : null}
            onLoaded={setLiveFaq}
          />



          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-primary/10 via-background to-accent/10 border border-primary/20 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-3 gradient-text">
              Ready to evaluate fit?
            </h2>
            <p className="text-foreground/75 mb-6 max-w-xl mx-auto">
              Book a 25-minute discovery call and let's see if AGAPE is the right partner for your next campaign.
            </p>
            <a
              href="https://calendly.com/dariusz-potepa-agapeco/discoverycall"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold px-6 py-3 rounded-lg shadow-lg hover:opacity-90 transition-opacity"
            >
              Schedule a 25-minute discovery call
            </a>
          </div>

        </div>
      </article>


      {/* Related */}
      {relatedPosts.length > 0 && (
        <section className="pb-20">
          <div className="container mx-auto px-6 max-w-6xl">
            <RelatedPosts posts={relatedPosts} />
          </div>
        </section>
      )}

      <Footer />
      <StickyArticleCTA postId={post.id} postSlug={post.slug} postTitle={post.title} />
    </div>
  );
};

export default BlogPost;
